import { useEffect, useRef, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { CloudArrowUp, Trash, Eye, FileText } from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const FILE_TAGS = ["Technical Spec", "Drawing", "Standards", "Enquiry", "Corrigendum", "Other"];
const CORRIGENDUM_RE = /corrigendum|corrigenda|amendment|addendum|errata/i;

export default function UploadStep({ projectId, onFilesChanged }) {
  const [files, setFiles] = useState([]);
  const [queue, setQueue] = useState([]);
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const loadFiles = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/files`);
      setFiles(data);
      onFilesChanged?.(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { if (projectId) loadFiles(); }, [projectId]); // eslint-disable-line

  const enqueue = (fileList) => {
    const items = Array.from(fileList).map((f) => ({
      qid: `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
      file: f, tag: CORRIGENDUM_RE.test(f.name) ? "Corrigendum" : "Technical Spec", progress: 0, status: "queued",
    }));
    setQueue((q) => [...q, ...items]);
  };

  const setDocKind = async (fid, kind) => {
    try { await api.patch(`/files/${fid}`, { doc_kind: kind }); loadFiles(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const uploadAll = async () => {
    for (const item of queue.filter((q) => q.status === "queued")) {
      try {
        setQueue((q) => q.map((x) => (x.qid === item.qid ? { ...x, status: "uploading" } : x)));
        const fd = new FormData();
        fd.append("file", item.file);
        fd.append("tag", item.tag);
        await api.post(`/projects/${projectId}/files`, fd, {
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
            setQueue((q) => q.map((x) => (x.qid === item.qid ? { ...x, progress: pct } : x)));
          },
        });
        setQueue((q) => q.map((x) => (x.qid === item.qid ? { ...x, status: "done", progress: 100 } : x)));
      } catch (e) {
        toast.error(`${item.file.name}: ${fmtErr(e)}`);
        setQueue((q) => q.map((x) => (x.qid === item.qid ? { ...x, status: "error" } : x)));
      }
    }
    loadFiles();
  };

  const deleteFile = async (fid) => {
    try { await api.delete(`/files/${fid}`); loadFiles(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const openPreview = async (f) => {
    try {
      const { data } = await api.get(`/files/${f.id}/preview`);
      setPreview({ ...data, name: f.filename });
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="intake-upload">
      <div
        data-testid="upload-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); enqueue(e.dataTransfer.files); }}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-sm p-10 text-center cursor-pointer transition-colors duration-150 ${
          dragOver ? "border-[var(--c-accent)] bg-[var(--c-accent)]/5" : "border-[var(--c-border)] bg-[var(--c-surface)] hover:border-[var(--c-muted)]"}`}
      >
        <CloudArrowUp size={28} className="mx-auto text-[var(--c-muted)]" />
        <div className="text-sm text-[var(--c-text)] mt-3">Drag & drop client documents here, or click to browse</div>
        <div className="text-[11px] font-mono text-[var(--c-muted)] mt-1.5 tracking-wider">PDF · XLSX · DOCX · DWG · DXF · JPG · PNG · TIFF</div>
        <input ref={fileInputRef} type="file" multiple data-testid="upload-input" className="hidden"
          accept=".pdf,.xlsx,.docx,.dwg,.dxf,.jpg,.jpeg,.png,.tiff,.tif"
          onChange={(e) => { enqueue(e.target.files); e.target.value = ""; }} />
      </div>

      {queue.length > 0 && (
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4 space-y-2" data-testid="upload-queue">
          {queue.map((q, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`queue-item-${i}`}>
              <FileText size={15} className="text-[var(--c-muted)] shrink-0" />
              <span className="text-xs text-[var(--c-text)] flex-1 truncate">{q.file.name}</span>
              <Select value={q.tag} onValueChange={(v) => setQueue((qs) => qs.map((x, xi) => (xi === i ? { ...x, tag: v } : x)))}>
                <SelectTrigger data-testid={`queue-tag-${i}`} className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-7 text-xs w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                  {FILE_TAGS.map((t) => <SelectItem key={t} value={t} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="w-28 h-1.5 bg-[var(--c-border)] rounded-sm overflow-hidden">
                <div className={`h-full transition-all duration-150 ${q.status === "error" ? "bg-[var(--c-red)]" : "bg-[var(--c-accent)]"}`} style={{ width: `${q.progress}%` }} />
              </div>
              <span className="font-mono text-[10px] text-[var(--c-muted)] w-14 text-right">
                {q.status === "done" ? "DONE" : q.status === "error" ? "ERROR" : q.status === "uploading" ? `${q.progress}%` : "QUEUED"}
              </span>
              <button onClick={() => setQueue((qs) => qs.filter((_, xi) => xi !== i))} className="p-1 text-[var(--c-muted)] hover:text-[var(--c-red)]"><Trash size={13} /></button>
            </div>
          ))}
          <div className="flex justify-end pt-2">
            <Button data-testid="upload-all-btn" onClick={uploadAll}
              disabled={!queue.some((q) => q.status === "queued")}
              className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
              Upload {queue.filter((q) => q.status === "queued").length} file(s)
            </Button>
          </div>
        </div>
      )}

      {files.length > 0 && (
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm" data-testid="uploaded-files">
          <div className="px-4 py-2.5 border-b border-[var(--c-border)] text-xs font-medium text-[var(--c-text)]">Uploaded · {files.length}</div>
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-[var(--c-border)]/60 last:border-0" data-testid={`file-row-${f.id.slice(0, 8)}`}>
              <FileText size={15} className="text-[var(--c-accent)] shrink-0" />
              <span className="text-xs text-[var(--c-text)] flex-1 truncate">{f.filename}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-muted)]">{f.tag}</span>
              <Select value={f.doc_kind || "base"} onValueChange={(v) => setDocKind(f.id, v)}>
                <SelectTrigger data-testid={`file-kind-${f.id.slice(0, 8)}`}
                  className={`bg-[var(--c-bg)] h-7 text-[10px] w-32 rounded-sm ${f.doc_kind === "corrigendum" ? "border-[var(--c-amber)] text-[var(--c-amber)]" : "border-[var(--c-border)] text-[var(--c-muted)]"}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                  <SelectItem value="base" className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">Base document</SelectItem>
                  <SelectItem value="corrigendum" className="text-[var(--c-amber)] focus:bg-[var(--c-surface-2)]">Corrigendum</SelectItem>
                </SelectContent>
              </Select>
              <span className="font-mono text-[10px] text-[var(--c-muted)]">{(f.size / 1024).toFixed(0)} KB</span>
              <button data-testid={`file-preview-${f.id.slice(0, 8)}`} onClick={() => openPreview(f)} className="p-1 text-[var(--c-muted)] hover:text-[var(--c-accent)]"><Eye size={14} /></button>
              <button data-testid={`file-delete-${f.id.slice(0, 8)}`} onClick={() => deleteFile(f.id)} className="p-1 text-[var(--c-muted)] hover:text-[var(--c-red)]"><Trash size={13} /></button>
            </div>
          ))}
        </div>
      )}

      {files.length === 0 && queue.length === 0 && (
        <div className="text-center text-xs text-[var(--c-muted)] py-2" data-testid="intake-upload-empty">
          No documents yet — upload client specs, drawings or standards, then run AI Parsing.
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="file-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--c-text)] text-sm font-mono">{preview?.name}</DialogTitle>
            <DialogDescription className="sr-only">File preview</DialogDescription>
          </DialogHeader>
          {preview?.type === "table" && (
            <div className="overflow-x-auto border border-[var(--c-border)] rounded-sm">
              <table className="w-full text-xs" data-testid="preview-table">
                <thead>
                  <tr className="border-b border-[var(--c-border)]">
                    {preview.columns.map((c, i) => <th key={i} className="text-left px-3 py-2 text-[10px] font-mono uppercase text-[var(--c-muted)]">{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className="border-b border-[var(--c-border)]/50 last:border-0">
                      {r.map((c, j) => <td key={j} className="px-3 py-1.5 text-[var(--c-text)] font-mono">{c}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview?.type === "pdf" && <iframe src={preview.url} title="pdf" className="w-full h-[65vh] border border-[var(--c-border)] rounded-sm bg-white" data-testid="preview-pdf" />}
          {preview?.type === "image" && <img src={preview.url} alt={preview.name} className="max-w-full border border-[var(--c-border)] rounded-sm" data-testid="preview-image" />}
          {preview?.type === "unsupported" && (
            <div className="text-xs text-[var(--c-muted)] py-8 text-center" data-testid="preview-unsupported">
              Inline preview not available for this file type. Download to view.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
